import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Checkbox } from '@xing-com/checkbox';
import { Flag } from '@xing-com/flag';
import { IconArrowDown, IconArrowUp } from '@xing-com/icons';
import { Link } from '@xing-com/link';
import { BodyCopy } from '@xing-com/typography';

import type { SearchParams, SetSearchParams } from '../../hooks';
import {
  FilterContainer,
  CheckboxContainer,
  StyledLabel,
  IconContainer,
  ShowMoreButton,
  ShowMoreContent,
} from '../shared-styles';

type FilterCheckboxProps = {
  aggKey: string;
  aggKeyText: string | null;
  filterData: Array<{
    count?: number;
    label: {
      localizationValue: string;
    };
    value: number | string;
  }>;
  searchParams: SearchParams;
  setSearchParams: SetSearchParams;
  upsellLink?: string;
};

export const FilterCheckbox: React.FC<FilterCheckboxProps> = ({
  aggKey,
  aggKeyText,
  filterData,
  searchParams,
  setSearchParams,
  upsellLink = '',
}) => {
  const [isShowMore, setIsShowMore] = React.useState(false);
  const queryKey = aggKey;
  const queryValue = (searchParams[queryKey] || '')
    .toString()
    .split(',')
    .filter(Boolean);
  const hasShowMoreButton = filterData.length > 3;

  const handleOnClick = (aggValue: string, isSelected: boolean): void => {
    if (isSelected) {
      const newParams = {
        ...searchParams,
        [queryKey]: queryValue.filter((value) => value !== aggValue).join(','),
      };
      setSearchParams(newParams);
    } else {
      const newParams = {
        ...searchParams,
        [queryKey]: [...queryValue, aggValue].join(','),
      };
      setSearchParams(newParams);
    }
  };

  const areMoreFiltersSelected = filterData
    .slice(3)
    .some((agg) => queryValue.includes(agg.value.toString()));

  return (
    <FilterContainer data-testid={aggKey}>
      <BodyCopy fontWeight="bold">
        {aggKeyText}&ensp;
        {upsellLink.length ? (
          <Link to={upsellLink} data-testid="premium-upsell">
            <Flag size="small" variant="premium" />
          </Link>
        ) : null}
      </BodyCopy>
      <CheckboxContainer>
        {filterData.slice(0, 3).map((agg, i) => {
          const aggValue = agg.value.toString();
          const isSelected = queryValue.includes(aggValue);
          return (
            <StyledLabel key={i}>
              <Checkbox
                on={isSelected}
                disabled={Boolean(upsellLink)}
                onChange={() => handleOnClick(aggValue, isSelected)}
              />
              <span style={{ opacity: upsellLink ? '0.4' : 1 }}>
                {agg.count
                  ? `${agg.label.localizationValue} (${agg.count})`
                  : agg.label.localizationValue}
              </span>
            </StyledLabel>
          );
        })}

        {hasShowMoreButton &&
          (isShowMore ? (
            <>
              {filterData.slice(3).map((agg, i) => {
                const aggValue = agg.value.toString();
                const isSelected = queryValue.includes(aggValue);
                return (
                  <StyledLabel key={i}>
                    <Checkbox
                      on={isSelected}
                      disabled={Boolean(upsellLink)}
                      onChange={() => handleOnClick(aggValue, isSelected)}
                    />
                    {agg.count
                      ? `${agg.label.localizationValue} (${agg.count})`
                      : agg.label.localizationValue}
                  </StyledLabel>
                );
              })}
              <ShowMoreButton
                disabled={areMoreFiltersSelected}
                data-testid="show-less-button"
                onClick={() => setIsShowMore(false)}
                aria-expanded={isShowMore}
                fontWeight="regular"
                size="small"
              >
                <ShowMoreContent>
                  <span>
                    <FormattedMessage id="SE_FILTERS_SHOW_LESS" />
                  </span>
                  <IconContainer>
                    <IconArrowUp width={18} height={18} />
                  </IconContainer>
                </ShowMoreContent>
              </ShowMoreButton>
            </>
          ) : (
            <ShowMoreButton
              data-testid="show-more-button"
              onClick={() => setIsShowMore(true)}
              aria-expanded={isShowMore}
              fontWeight="regular"
              size="small"
              disabled={Boolean(upsellLink)}
            >
              {' '}
              <ShowMoreContent>
                <span>
                  <FormattedMessage id="SE_FILTERS_SHOW_MORE" />
                </span>
                <IconContainer>
                  <IconArrowDown width={18} height={18} />
                </IconContainer>
              </ShowMoreContent>
            </ShowMoreButton>
          ))}
      </CheckboxContainer>
    </FilterContainer>
  );
};
